import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { de } from 'date-fns/locale';
import { Trash2 } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { useEnhancedTimelineEntryManagement } from '@/hooks/useEnhancedTimelineEntryManagement';
import { InstagramCarousel } from '@/components/ui/InstagramCarousel';
import TimelineSidebar from './TimelineSidebar';
import TimelineSidebarBottomNav from './TimelineSidebarBottomNav';
import SocialMediaLikeButton from './SocialMediaLikeButton';
import UserProfileInfo from './UserProfileInfo';
import ButtonCircle from '@/components/ui/ButtonCircle';

interface TimelineSidebarEntryProps {
  isOpen: boolean;
  onClose: () => void;
  className?: string;
  targetUserProfile?: any;
  viewEntryId?: string;
}

const TimelineSidebarEntry = ({
  isOpen,
  onClose,
  className,
  targetUserProfile,
  viewEntryId
}: TimelineSidebarEntryProps) => {
  const { user } = useAuth();
  const { t } = useTranslation(['memory', 'common', 'timeline']);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  // Fetch entry data when in view mode with creator profile info
  const { data: viewEntry, isLoading: viewEntryLoading } = useQuery({
    queryKey: ['timeline-entry', viewEntryId],
    queryFn: async () => {
      if (!viewEntryId) return null;
      const { data, error } = await supabase
        .from('profile_timeline_entries')
        .select(`
          *,
          profile_timeline_sections:profile_timeline_section_id (
            id,
            name,
            name_en,
            description,
            description_en
          ),
          profiles!timeline_entries_creator_id_fkey (
            id,
            username,
            first_name,
            last_name,
            profile_picture
          )
        `)
        .eq('id', viewEntryId)
        .single();
      
      if (error) throw error;
      return data;
    },
    enabled: !!viewEntryId
  });

  const { deleteEntry, isDeleting, canDeleteEntry, getDeleteButtonText } = useEnhancedTimelineEntryManagement(targetUserProfile?.id);

  const handleEntryEdit = () => {
    // TODO: Implement edit functionality
    toast.info("Edit functionality coming soon");
  };

  const handleEntryDelete = () => {
    setShowDeleteConfirm(true);
  };

  const confirmDelete = async () => {
    if (!user?.id || !viewEntry) {
      console.error('User not authenticated or no entry');
      return;
    }
    
    const success = await deleteEntry(viewEntry.id, user.id, viewEntry.creator_id, viewEntry.user_id);
    if (success) {
      setShowDeleteConfirm(false);
      onClose();
    }
  };

  const handleEntryShare = async () => {
    try {
      if (navigator.share) {
        await navigator.share({
          title: viewEntry?.title || 'Timeline Entry',
          text: 'Check out this timeline entry!',
          url: window.location.href
        });
      } else {
        // Fallback: copy to clipboard
        await navigator.clipboard.writeText(window.location.href);
        toast.success('Link copied to clipboard!');
      }
    } catch (error) {
      console.error('Error sharing:', error);
      toast.error('Error sharing entry');
    }
  };

  // Check permissions using enhanced logic
  const canEditEntry = viewEntry && (viewEntry.creator_id === user?.id);
  const canDelete = user?.id && viewEntry ? canDeleteEntry(viewEntry.creator_id, viewEntry.user_id, user.id) : false;

  const renderViewContent = () => {
    if (viewEntryLoading) {
      return (
        <div className="flex justify-center items-center p-8">
          <div className="text-gray-500">Loading entry...</div>
        </div>
      );
    }

    if (!viewEntry) {
      return (
        <div className="flex flex-col items-center justify-center p-4 text-center">
          <div className="text-red-500 mb-2">Entry not found</div>
          <p className="text-gray-500 text-sm">This entry may have been deleted or you don't have permission to view it.</p>
        </div>
      );
    }

    const formatGermanDate = (date: string) => {
      return `Erinnerung vom ${format(new Date(date), 'dd.MM.yyyy', { locale: de })}`;
    };

    const getDateDisplay = () => {
      if (viewEntry.date) {
        return formatGermanDate(viewEntry.date);
      } else if (viewEntry.year) {
        if (viewEntry.month && viewEntry.day) {
          const dateStr = `${viewEntry.year}-${String(viewEntry.month).padStart(2, '0')}-${String(viewEntry.day).padStart(2, '0')}`;
          return formatGermanDate(dateStr);
        }
        return `Jahr ${viewEntry.year}`;
      }
      return 'Unbekanntes Datum';
    };

    const timelineTitle = targetUserProfile 
      ? `${targetUserProfile.first_name || targetUserProfile.username}'s timeline (${targetUserProfile.birthdate ? new Date().getFullYear() - new Date(targetUserProfile.birthdate).getFullYear() : ''} Jahre)`
      : 'Timeline';
    
    const sectionName = viewEntry.profile_timeline_sections?.name;
    const isOwnEntry = viewEntry.creator_id === user?.id;

    return (
      <div>
          {/* Image Section - Full width, prominent display */}
          {viewEntry.photos && viewEntry.photos.length > 0 && (
            <div className="w-full">
              <InstagramCarousel
                images={viewEntry.photos}
                className="w-full"
                showControls={true}
                showDots={true}
              />
            </div>
          )}

          {/* Social Media Metadata Section */}
          <div className="px-4 py-3 border-b border-gray-100">
            <div className="flex items-center justify-between">
              <SocialMediaLikeButton count={0} />
              <UserProfileInfo 
                creatorProfile={viewEntry.profiles}
                isOwnEntry={isOwnEntry}
              />
            </div>
          </div>

          {/* Entry Content */}
          <div className="px-4 py-4 space-y-4">
            {/* Entry Title */}
            <h1 className="text-xl font-bold text-gray-900">
              {viewEntry.title}
            </h1>

            {/* German Date */}
            <p className="text-sm text-muted-foreground">
              {getDateDisplay()}
            </p>

            {/* Entry Description */}
            {viewEntry.content && (
              <div className="space-y-2">
                <p className="text-gray-700 whitespace-pre-wrap leading-relaxed">
                  {viewEntry.content}
                </p>
              </div>
            )}
          </div>
        </div>
    );
  };

  const footerContent = (
    <TimelineSidebarBottomNav
      mode="view"
      onShare={handleEntryShare}
      onEdit={handleEntryEdit}
      onDelete={handleEntryDelete}
      canEdit={canEditEntry}
      canDelete={canDelete}
      entryTitle={viewEntry?.title}
    />
  );

  // Create header content for the sidebar
  const timelineTitle = viewEntry && targetUserProfile 
    ? `${targetUserProfile.first_name || targetUserProfile.username}'s timeline`
    : 'Timeline';
  
  const subtitle = targetUserProfile && targetUserProfile.birthdate
    ? `${new Date().getFullYear() - new Date(targetUserProfile.birthdate).getFullYear()} Jahre`
    : '';
  
  const sectionName = viewEntry?.profile_timeline_sections?.name;
  
  const headerContent = (
    <div className="flex flex-col">
      {subtitle && (
        <div className="text-sm text-gray-500">
          {subtitle}
        </div>
      )}
      {sectionName && (
        <div className="text-sm text-gray-600 mt-1">
          {sectionName}
        </div>
      )}
    </div>
  );

  // Render delete confirmation content
  const renderDeleteConfirmationContent = () => (
    <div className="px-4 py-6 text-center">
      <div className="flex flex-col items-center space-y-4">
        <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center">
          <Trash2 className="h-8 w-8 text-red-500" />
        </div>
        <div className="space-y-2">
          <h3 className="text-lg font-semibold text-gray-900">
            Delete "{viewEntry?.title}"?
          </h3>
          <p className="text-gray-600 text-sm max-w-sm">
            Are you sure you want to delete this entry? This action cannot be undone and will permanently remove the entry from the timeline.
          </p>
        </div>
      </div>
    </div>
  );

  // Delete confirmation footer
  const deleteConfirmationFooter = (
    <div className="bg-white/20 backdrop-blur-lg border-t border-white/20 shadow-lg">
      <div className="flex justify-between items-center py-4 px-8 gap-4">
        <button
          onClick={() => setShowDeleteConfirm(false)}
          disabled={isDeleting}
          className="flex-1 h-12 px-4 rounded-lg border border-white/20 bg-white/10 text-gray-700 hover:bg-white/20 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          Cancel
        </button>
        
        <button
          onClick={confirmDelete}
          disabled={isDeleting}
          className="flex-1 h-12 px-4 rounded-lg bg-red-500 text-white hover:bg-red-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
        >
          {isDeleting ? (
            <>
              <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
              Deleting...
            </>
          ) : (
            <>
              <Trash2 className="h-4 w-4" />
              {viewEntry ? getDeleteButtonText(viewEntry.creator_id, viewEntry.user_id, user?.id || '') : 'Delete'}
            </>
          )}
        </button>
      </div>
    </div>
  );

  // Delete confirmation header
  const deleteConfirmationHeader = (
    <div className="text-sm text-red-600 mt-1">
      This action cannot be undone
    </div>
  );

  return (
    <TimelineSidebar
      isOpen={isOpen}
      onClose={showDeleteConfirm ? () => setShowDeleteConfirm(false) : onClose}
      className={className}
      title={showDeleteConfirm ? "Delete Entry" : timelineTitle}
      headerContent={showDeleteConfirm ? deleteConfirmationHeader : headerContent}
      footerContent={showDeleteConfirm ? deleteConfirmationFooter : footerContent}
    >
      {showDeleteConfirm ? renderDeleteConfirmationContent() : renderViewContent()}
    </TimelineSidebar>
  );
};

export default TimelineSidebarEntry;